(ns common.localization
  (:require
   [common.currencies :as currencies]
   [common.gender :as gender]
   [common.i18n.core :as i18n]
   [common.i18n.plural :as plural]
   [common.util :as cu]
   [common.schema.entities.kyc.options :as kyc-options]
   [common.languages :as languages]
   [common.country :as country]
   [common.modules :as modules]
   [common.schema.entities.message-options :as message-options]
   [common.schema.entities.task-type :as task-type]
   [common.status :as status]
   [common.holding-exit-status :as holding-exist-status]
   [common.log :as log]
   [common.localization-en :as en]
   [common.localization-fi :as fi]
   #?(:cljs [goog.string :as gs])
   [common.schema.registry :as registry]
   [common.schema.entity-type-names :as entity-type-names]
   [common.schema.entities.decision-participant-options :as decision-participant-options]
   [common.value-creation-status :as value-creation-status]
   [common.schema.entities.esign-process-status :as esign-process-status]
   [common.schema.entities.decision-options :as decision-options]
   [common.tol-a :as tol-a]
   [common.tol-b :as tol-b]
   #?(:cljs [sentry.integration :as sentry])))

(defonce missing (atom {}))

(defn- local-debug-localization?
  []
  #?(:cljs (let [hostname (some-> js/window .-location .-hostname)]
             (contains? #{"localhost" "127.0.0.1" "::1" "0.0.0.0"} hostname))
     :clj true))

(defn- missing-localization-fallback
  [k]
  (if (local-debug-localization?)
    (str "Not yet localized <" k ">")
    (if (keyword? k)
      (name k)
      "")))

(defn- normalize-format-args
  [format-args]
  (cond
    (nil? format-args) nil
    (string? format-args) [format-args]
    (sequential? format-args) format-args
    :else [format-args]))

(defn- schema-i18n-key [attribute]
  (when-let [fs (registry/get-field-schema nil attribute)]
    (:i18n/key fs)))

(defn translate-
  [k format-args lang]
  (let [format-args (normalize-format-args format-args)
        reported-missing (or (get @missing k)
                             (get @missing (str k)))
        dict (case lang
               :en en/translations
               :fi fi/translations)
        s (get dict k)]
    (cond
      (or (not (keyword? k))
          (= k (keyword "")))
      ""
      s (if (seq format-args)
          (apply #?(:cljs gs/format
                    :clj format)
                 s
                 format-args)
          s)
      reported-missing (missing-localization-fallback k)
      :else (do
              (swap! missing assoc k k)
              (log/warn (str "LOCALIZATION MISSING : [" k "] -> <" s ">"))
              #?(:cljs (sentry/report-missing-localization! k lang))
              (missing-localization-fallback k)))))

(defn has-translation?
  [k]
  (let [en (entity-type-names/entities k)
        fs (registry/get-field-schema nil k)]
    (or
     (->> en
          :field/translations
          :en)
     (->> fs
          :field/translations
          :en)
     (get en/translations k))))

(defn tr
  ([k] (tr k nil :en))
  ([k fmt-args] (tr k fmt-args :en))
  ([k fmt-args lang]
   (let [lang (or (some #{lang} [:en :fi]) :en)]
     (or
      ;; 1) schema i18n key
      (when-let [ik (schema-i18n-key k)]
        (let [s (i18n/t ik lang)]
          (when s
            (if-let [fmt-args (seq (normalize-format-args fmt-args))]
              (#?(:cljs goog.string/format :clj format) s (into [] fmt-args))
              s))))
      ;; 2) your existing resolution path
      (let [en (entity-type-names/entities k)]
        (-> en :field/translations (get lang)))
      (let [fs (registry/get-field-schema nil k)]
        (-> fs :field/translations (get lang)))
      ;; 3) global dicts
      #?(:cljs (try (translate- k fmt-args lang)
                    (catch js/Object o (log/error "Error translating" k o)))
         :clj  (translate- k fmt-args lang))))))

(defn tr-plural
  "Translate a number with the plural form of its language.
   translation-key is the base key, for example :kanban-column-item-count. The
   dictionary has one key for each plural category, for example
   :kanban-column-item-count/one. Try the form of the category of quantity,
   then the :other form, then the same two steps in English. A nil quantity
   counts as 0. The quantity is the only format argument. See
   `common.i18n.plural`."
  [{:keys [translation-key quantity lang]}]
  (let [lang      (if (contains? i18n/supported-locales lang)
                    lang
                    :en)
        quantity  (or quantity 0)
        found-key (cu/find-first (fn [k]
                                   (some? (i18n/t k lang)))
                                 (plural/form-keys {:translation-key translation-key
                                                    :lang            lang
                                                    :quantity        quantity}))]
    (cond
      found-key
      (tr found-key [quantity] lang)

      (not= lang :en)
      (tr-plural {:translation-key translation-key
                  :quantity        quantity
                  :lang            :en})

      :else
      (tr (plural/form-key translation-key :other) [quantity] lang))))

(defn tr-item-count
  "Return the label for a number of items, for example \"1 item\" or \"2 items\"."
  ([n]
   (tr-item-count n :en))
  ([n lang]
   (tr-plural {:translation-key :kanban-column-item-count
               :quantity        n
               :lang            lang})))

(defn label
  ([k]
   (label k nil))
  ([k format-args]
   (label k format-args :en))
  ([k format-args lang]
   (let [lang (or (some #{lang} [:en :fi])
                  (case lang
                    :FI :fi
                    :EN :en
                    :FIN :fi
                    :ENG :en
                    :en))]
     (or
      (let [en (entity-type-names/entities k)]
        (-> en
            :field/translations
            (get lang)))
      (let [fs (registry/get-field-schema nil k)]
        (-> fs
            :field/translations
            (get lang)))
      (currencies/get-label k)
      (languages/get-label k)
      (country/get-label k)
      (modules/get-label k)
      (tol-a/get-label k)
      (tol-b/get-label k)
      (status/get-label k)
      (holding-exist-status/get-label k)
      (message-options/get-label k)
      (gender/get-label k)
      (kyc-options/process-status-label k)
      (task-type/get-label k)
      (decision-participant-options/get-label k)
      (value-creation-status/get-label k)
      (kyc-options/label k)
      (esign-process-status/get-label k)
      (decision-options/get-label k)
      #?(:cljs
         (try
           (translate- k format-args lang)
           (catch js/Object o
             (log/error "Error translating" k o)))
         :clj (translate- k format-args lang))))))

(defn label-values
  [kw-coll]
  (map (fn [s]
         {:value s
          :label (tr s)})
       kw-coll))

(defn field-name
  ([attribute field-schema]
   (field-name attribute field-schema false))
  ([attribute field-schema short?]
   (or (some-> field-schema
               :field/translations
               :en)
       (some-> field-schema
               :translations
               :en)
       (some-> field-schema
               :field/name)
       (tr attribute))))

(defn get-label
  [field-schema attribute tenant]
  (or (->> field-schema
           :field/translations
           :en)
      (tr attribute)
      (when-let [fs (registry/get-field-schema tenant attribute)]
        (field-name attribute fs))))
